import { Component, OnInit, ViewEncapsulation, AfterViewInit, OnDestroy, ElementRef, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { TenantService } from '../../services/tenant.service';
import { Firestore, doc, getDoc, collection, getDocs, setDoc } from '@angular/fire/firestore';
import { Course } from '../../models/course.model';
import { Store } from '@ngrx/store';
import { AppState } from '../../store';
import { selectTenantInfo, selectUser } from '../../store/selectors';
import { User } from '../../models/user.model';
import { TranslateModule } from '@ngx-translate/core';
import { UrlBuilderService } from '../../services/url-builder.service';
import { DigitalAssetsSvgSpriteComponent } from '../../digital-assets-dialog/digital-assets-svg-sprite.component';
import { take, takeUntil } from 'rxjs/operators';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DigitalAssetsDialogComponent } from '../../digital-assets-dialog/digital-assets-dialog.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ArrowLeftIconSvgComponent } from '../../svg-sprites/general/arrow-left-icon-svg.component';
import { ScormApiService } from '../../services/scorm-api.service';
import { Subject } from 'rxjs';
import { FullScreenToggleService } from '../../services/full-screen-toggle.service';

// Update the User model interface to include uid
interface UserWithUID extends User {
  // uid is no longer needed, since User already has an id property
}

@Component({
  selector: 'watch-scorm-course',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DigitalAssetsSvgSpriteComponent,
    MatDialogModule,
    MatProgressSpinnerModule,
    ArrowLeftIconSvgComponent
],
  templateUrl: './watch-scorm-course.component.html',
  styleUrls: ['./watch-scorm-course.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class WatchScormCourseComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('scormFrame') scormFrame: ElementRef | undefined;
  
  courseId: string;
  courseTitle: string = '';
  course: Course;
  user: User;
  tenant: any;
  
  scormLaunchUrl: string | null = null;
  safeScormUrl: SafeResourceUrl;
  scormError: string | null = null;
  isScormLoading = false;
  scormLaunched = false;
  
  hasCompletedCourse: boolean = false;
  
  private scormVersion: string = '';
  private progressCheckInterval: any;
  private apiCheckInterval: any;
  private destroyed$ = new Subject<void>();
  
  // Tracked SCORM data
  private scormTrackedData = {
    'cmi.core.lesson_status': '',
    'cmi.core.score.raw': '',
    'cmi.core.lesson_location': '',
    'cmi.suspend_data': '',
    'cmi.core.session_time': '',
    'cmi.core.exit': ''
  };

  // Course lessons data
  private courseLessons: any[] = [];
  private lessonsWatched: string[] = [];

  // Debug API calls
  apiCalls: string[] = [];

  // Track full-screen state
  private isFullScreen = false;

  certificateImgUrl: string;
  fullScreenImgUrl: string;

  constructor(
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private router: Router,
    private tenantService: TenantService,
    private firestore: Firestore,
    private store: Store<AppState>,
    private urlBuilder: UrlBuilderService,
    private dialog: MatDialog,
    private scormApiService: ScormApiService,
    private fullScreenToggle: FullScreenToggleService
  ) {}

  ngOnInit() {
    this.certificateImgUrl = this.urlBuilder.buildImageUrl(
      'images/course-player/certificate.svg'
    );
    this.fullScreenImgUrl = this.urlBuilder.buildImageUrl(
      'images/course-player/fullscreen-blue.svg'
    );

    this.store.select(selectUser).pipe(take(1)).subscribe(user => {
      this.user = user;
    });
    
    // Listen to full-screen toggle events from other components (e.g., Top Menu)
    this.fullScreenToggle.fullScreen$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((shouldBeFullScreen) => {
        if (shouldBeFullScreen && !this.isFullScreen) {
          this.enterFullScreen();
        } else if (!shouldBeFullScreen && this.isFullScreen) {
          this.exitFullScreen();
        }
      });

    this.store.select(selectTenantInfo).pipe(take(1)).subscribe(tenant => {
      this.tenant = tenant;
    });
    
    this.route.params.subscribe(params => {
      this.courseId = params['courseId'];
      if (this.courseId) {
        this.route.queryParams.subscribe(queryParams => {
          if (queryParams['title']) {
            this.courseTitle = decodeURIComponent(queryParams['title']);
          }
          this.loadScormContent();
          this.loadCourseData();
        });
      } else {
        this.scormError = 'Missing course ID';
        this.isScormLoading = false;
      }
    });
    
    // Subscribe to SCORM API data changes
    this.scormApiService.dataChange$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(change => {
        this.handleScormDataChange(change.parameter, change.newValue, change.oldValue);

        // Auto-complete when progress >= 70 % and status still incomplete
        if (change.parameter === 'cmi.progress_measure') {
          const pct = parseFloat(change.newValue);
          if (!this.hasCompletedCourse && !isNaN(pct) && pct >= 0.7) {
            console.log(`Progress reached ${(pct*100).toFixed(1)}% — forcing completion`);
            this.simulateCompletion();
          }
        }
      });
    
    // Subscribe to completion events
    this.scormApiService.completion$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(status => {
        console.log(`Completion status from SCORM API: ${status}`);
        if (!this.hasCompletedCourse) {
          this.hasCompletedCourse = true;
          this.saveCompletionToDatabase();
        }
      });
  }
  
  ngAfterViewInit() {
    const mainContentElement = document.querySelector('main');
    if (mainContentElement) {
      mainContentElement.style.padding = '0';
      mainContentElement.style.margin = '0';
      mainContentElement.style.maxWidth = '100%';
      mainContentElement.style.width = '100%';
    }
    
    this.progressCheckInterval = setInterval(() => {
      this.checkAndUpdateProgress();
    }, 10000);
  }
  
  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    
    if (this.progressCheckInterval) {
      clearInterval(this.progressCheckInterval);
    }
    
    if (this.apiCheckInterval) {
      clearInterval(this.apiCheckInterval);
    }
  }
  
  private loadScormContent() {
    console.log('==== LOADING SCORM CONTENT ====');
    console.log('Starting to load SCORM content for course:', this.courseId);
    this.isScormLoading = true;
    this.scormError = null;

    const tenantId = this.tenantService.id;
    console.log('Tenant ID:', tenantId);
    
    if (!tenantId || !this.courseId) {
      this.scormError = 'Missing tenant ID or course ID';
      this.isScormLoading = false;
      console.error('Missing tenant ID or course ID');
      return;
    }
    
    if (!this.courseTitle) {
      this.loadCourseTitleFromDatabase(tenantId);
    }
    
    try {
      const bucket = environment.firebase.scormCourseBucket;
      console.log('SCORM bucket:', bucket);
      
      // Add cache-busting parameter to prevent getting cached versions of deleted files
      const timestamp = new Date().getTime();
      const manifestUrl = `https://storage.googleapis.com/${bucket}/scorm/${tenantId}/${this.courseId}/scormFiles/imsmanifest.xml?cachebust=${timestamp}`;
      
      console.log('Fetching manifest from URL:', manifestUrl);
      
      fetch(manifestUrl)
        .then(response => {
          console.log('Manifest fetch response status:', response.status);
          if (!response.ok) {
            throw new Error(`Failed to fetch manifest file (status: ${response.status})`);
          }
          return response.text();
        })
        .then(manifestXml => {
          console.log('Manifest XML fetched successfully, length:', manifestXml.length);
          
          // Log a truncated version of the XML for debugging
          const truncatedXml = manifestXml.length > 200 ? 
                              manifestXml.substring(0, 100) + '...' + manifestXml.substring(manifestXml.length - 100) : 
                              manifestXml;
          console.log('Manifest XML (truncated):', truncatedXml);
          
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(manifestXml, 'text/xml');
          
          if (!this.courseTitle) {
            this.extractCourseTitle(xmlDoc);
            console.log('Extracted course title:', this.courseTitle);
          }
          
          const resources = xmlDoc.getElementsByTagName('resource');
          console.log('Number of resources in manifest:', resources.length);
          
          let launchPath = '';
          for (let i = 0; i < resources.length; i++) {
            const resource = resources[i];
            const scormType1 = resource.getAttribute('adlcp:scormtype');
            const scormType2 = resource.getAttribute('adlncp:scormtype');
            const scormType2004 = resource.getAttribute('adlcp:scormType');
            
            const href = resource.getAttribute('href');
            
            console.log(`Resource ${i}: href=${href}, scormtype1=${scormType1}, scormtype2=${scormType2}, scormtype2004=${scormType2004}`);

            if (href && 
                ((scormType1 && scormType1.toLowerCase() === 'sco') || 
                 (scormType2 && scormType2.toLowerCase() === 'sco') ||
                 (scormType2004 && scormType2004.toLowerCase() === 'sco'))) {
              launchPath = href;
              console.log('Found SCO resource with href:', launchPath);
              break;
            }
          }

          if (!launchPath && resources.length > 0) {
            const firstResourceHref = resources[0].getAttribute('href');
            if (firstResourceHref) {
                console.warn('No resource with scormtype="sco" found, falling back to the first resource href.');
                launchPath = firstResourceHref;
                console.log('Using first resource href as fallback:', launchPath);
            }
          }
          
          if (!launchPath) {
            throw new Error('Could not find any valid launch file in the SCORM package manifest.');
          }
          
          // Add cache-busting to the launch URL as well
          let scormUrl;
          if (this.tenant?.customDomain) {
            scormUrl = 'https://' + this.tenant.customDomain + '/scorm/' + tenantId + '/' + this.courseId + '/scormFiles/' + launchPath;
          } else {
            scormUrl = `https://storage.googleapis.com/${bucket}/${tenantId}/${this.courseId}/scormFiles/${launchPath}`;
          }
          this.scormLaunchUrl = `${scormUrl}?cachebust=${timestamp}`;
          console.log('Final SCORM launch URL:', this.scormLaunchUrl);
          
          this.isScormLoading = false;

          // Automatically launch the SCORM content now that we have the URL
          if (!this.scormLaunched) {
            console.log('Auto-launching SCORM course (no play button)');
            this.launchScormContent();
          }

          console.log('==== SCORM CONTENT LOADED SUCCESSFULLY ====');
        })
        .catch(error => {
          this.scormError = error.message || 'An unknown error occurred while loading SCORM content.';
          this.isScormLoading = false;
          console.error('Error loading SCORM content:', error);
          console.log('==== SCORM CONTENT LOADING FAILED ====');
        });
    } catch (error: any) {
      this.scormError = error.message || 'An unknown error occurred.';
      this.isScormLoading = false;
      console.error('Error in loadScormContent:', error);
      console.log('==== SCORM CONTENT LOADING FAILED ====');
    }
  }
  
  private handleScormMessage(event: MessageEvent) {
    if (!event.data || event.data.type !== 'scorm-track') return;
    
    const data = event.data;
    console.log('SCORM Track Message:', data);
    
    if (data.parameter && data.value !== undefined) {
      if (this.scormTrackedData.hasOwnProperty(data.parameter)) {
        const oldValue = this.scormTrackedData[data.parameter];
        const newValue = data.value;
        
        if (oldValue !== newValue) {
          this.scormTrackedData[data.parameter] = newValue;
          console.log(`SCORM Data Updated - ${data.parameter}: ${newValue}`);
          
          this.handleScormDataChange(data.parameter, newValue, oldValue);
        }
      }
    }
    
    if (data.action === 'init') {
      console.log(`SCORM ${data.version} tracking initialized`);
      this.scormVersion = data.version;
    }
  }
  
  private handleScormDataChange(parameter: string, newValue: any, oldValue: any) {
    // Only track these specific status parameters
    if (parameter === 'cmi.core.lesson_status' || parameter === 'cmi.completion_status') {
      console.log(`Processing ${parameter} change: ${oldValue} -> ${newValue}`);
      
      // Update our local tracking data
      if (this.scormTrackedData.hasOwnProperty(parameter)) {
        this.scormTrackedData[parameter] = newValue;
      }
      
      // Only mark as completed if status is "completed" or "passed"
      if (newValue === 'completed' || newValue === 'passed') {
        if (!this.hasCompletedCourse) {
          this.hasCompletedCourse = true;
          this.saveCompletionToDatabase();
          console.log(`Course marked as completed based on ${parameter} = ${newValue}`);
        }
      }
    }
  }
  
  private saveCompletionToDatabase() {
    console.log('==== SAVING COURSE COMPLETION TO DATABASE ====');
    console.log('Course ID:', this.courseId);
    
    const tenantId = this.tenantService.id;
    const userId = this.user?.id;
    
    if (!tenantId || !userId || !this.courseId) {
      console.error('Missing required data to save completion');
      return;
    }
    
    // Use the same path pattern as regular courses
    const lessonId = 'scorm-content'; // Use a constant ID for SCORM courses
    const lessonStatusPath = `schools/${tenantId}/users/${userId}/lessonsWatchedPerCourse/${this.courseId}/lessonsWatched/${lessonId}`;
    
    console.log('Database path:', lessonStatusPath);
    
    try {
      const lessonStatusDocRef = doc(this.firestore, lessonStatusPath);
      
      console.log('Setting data:', { watched: true });
      
      // Use setDoc to create the document if it doesn't exist
      setDoc(lessonStatusDocRef, {
        watched: true
      }).then(() => {
        console.log('✅ COURSE COMPLETION SAVED TO DATABASE SUCCESSFULLY');

        // Keep local tracking array in sync so that dialogs relying on it work correctly
        if (!this.lessonsWatched.includes(lessonId)) {
          this.lessonsWatched.push(lessonId);
        }

        console.log('====================================');
      }).catch(error => {
        console.error('❌ ERROR SAVING COMPLETION TO DATABASE:', error);
        console.log('====================================');
      });
    } catch (error) {
      console.error('Error in saveCompletionToDatabase:', error);
    }
  }
  
  private checkAndUpdateProgress() {
    // This is a periodic check to see if we're still getting SCORM data
    console.log('SCORM tracking check - current status data:', {
      'lesson_status': this.scormTrackedData['cmi.core.lesson_status'],
      'completion_status': this.scormTrackedData['cmi.completion_status'],
      'hasCompletedCourse': this.hasCompletedCourse
    });
  }
  
  triggerScormApiTest() {
    console.log('Manual SCORM API test triggered');
    this.logProgressDetails();
    
    // Get the latest API calls
    this.apiCalls = this.scormApiService.getApiCalls();
  }

  private saveProgressToDatabase(progress: number) {
    console.log(`Progress updated (${progress}%) - Database save skipped`);
  }

  logProgressDetails() {
    console.log('=== SCORM Progress Details ===');
    console.log('Has Completed Course:', this.hasCompletedCourse);
    console.log('SCORM Data (raw):', this.scormTrackedData);
    console.log('==========================');
  }

  async loadCourseData() {
    const tenantId = this.tenantService.id;
    
    if (!tenantId || !this.courseId) {
      console.error('Missing tenant ID or course ID for course data loading');
      return;
    }
    
    try {
      const courseDocRef = doc(this.firestore, `schools/${tenantId}/courses/${this.courseId}`);
      const courseDocSnap = await getDoc(courseDocRef);
      
      if (courseDocSnap.exists()) {
        this.course = courseDocSnap.data() as Course;
        this.course.id = this.courseId;
        
        await this.loadCourseLessons(tenantId);
        
        // Check if we already have user data before checking completion status
        if (this.user && this.user.id) {
          await this.loadUserCompletionStatus();
        } else {
          // Wait for user data to be available
          console.log('Waiting for user data before checking completion status...');
          this.store.select(selectUser).pipe(take(1)).subscribe(async user => {
            if (user) {
              this.user = user as UserWithUID;
              await this.loadUserCompletionStatus();
            } else {
              console.log('User data still not available after waiting');
            }
          });
        }
      }
    } catch (error) {
      console.error('Error loading course data:', error);
    }
  }
  
  async loadCourseLessons(tenantId: string) {
    try {
      const lessonsPath = `schools/${tenantId}/courses/${this.courseId}/lessons`;
      const lessonsRef = collection(this.firestore, lessonsPath);
      const lessonsSnapshot = await getDocs(lessonsRef);
      
      this.courseLessons = lessonsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        };
      });
      
      if (this.courseLessons.length === 0) {
        this.courseLessons = [{
          id: 'scorm-content',
          title: this.courseTitle || 'SCORM Course',
          type: 'scorm'
        }];
      }
      
    } catch (error) {
      console.error('Error loading course lessons:', error);
    }
  }
  
  async loadUserCompletionStatus() {
    console.log('==== CHECKING COMPLETION STATUS FROM DATABASE ====');
    
    if (!this.user || !this.user.id) {
      console.log('Missing user data for loading completion status');
      console.log('User object:', this.user ? 'exists' : 'null');
      console.log('User ID:', this.user?.id || 'not available');
      console.log('====================================');
      return;
    }
    
    if (!this.courseId) {
      console.log('Missing course ID for loading completion status');
      console.log('====================================');
      return;
    }
    
      const tenantId = this.tenantService.id;
    const userId = this.user.id;
    const lessonId = 'scorm-content'; // Use a constant ID for SCORM courses
    
    // Use the same path pattern as regular courses
    const lessonStatusPath = `schools/${tenantId}/users/${userId}/lessonsWatchedPerCourse/${this.courseId}/lessonsWatched/${lessonId}`;
    
    console.log('Checking database path:', lessonStatusPath);
    
    try {
      const lessonStatusDocRef = doc(this.firestore, lessonStatusPath);
      const lessonStatusDocSnap = await getDoc(lessonStatusDocRef);
      
      if (lessonStatusDocSnap.exists()) {
        const lessonStatusData = lessonStatusDocSnap.data();
        console.log('Found lesson status document:', lessonStatusData);
        
        if (lessonStatusData && lessonStatusData['watched'] === true) {
          this.hasCompletedCourse = true;

          // Ensure local lessonsWatched list contains the placeholder lesson id
          if (!this.lessonsWatched.includes(lessonId)) {
            this.lessonsWatched.push(lessonId);
          }

          console.log('✅ USER HAS ALREADY COMPLETED THIS COURSE (from database)');
        } else {
          console.log('Course not marked as completed in database');
          this.hasCompletedCourse = false;
        }
      } else {
        console.log('No lesson status document found in database');
        this.hasCompletedCourse = false;
      }
    } catch (error) {
      console.error('Error loading user completion status:', error);
    }
    
    console.log('Current hasCompletedCourse flag:', this.hasCompletedCourse);
    console.log('====================================');
  }
  
  openAssetsDialog() {
    if (!this.course) {
      console.error('Course data not available');
      return;
    }
    
    const dialogData = {
      course: this.course,
      lessonsWatched: this.lessonsWatched,
      lessons: this.courseLessons,
      tenant: this.tenant,
      isScormCourse: true,
      hasCompletedCourse: this.hasCompletedCourse,
      hidePercentage: true
    };
    
    const dialogRef = this.dialog.open(DigitalAssetsDialogComponent, {
      width: '720px',
      data: dialogData,
      disableClose: false
    });
    
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'getCertificate' && this.course.url) {
        this.router.navigate(['/certificates', this.course.url]);
      }
    });
  }

  goBack() {
    this.router.navigate(['/courses']);
  }

  simulateCompletion() {
    console.log('Manually simulating course completion');
    
    // Force completion via the service
    this.scormApiService.forceCompletion();
    
    // Update local status
    this.scormTrackedData['cmi.core.lesson_status'] = 'completed';
    
    // Set completion flag
    this.hasCompletedCourse = true;
    
    // Explicitly save to database
    this.saveCompletionToDatabase();
    
    console.log('Course completion simulated and saved to database');
  }

  // Add missing properties
  private extractCourseTitle(xmlDoc: Document): void {
    const organizations = xmlDoc.getElementsByTagName('organizations');
    if (organizations.length > 0) {
      const organization = organizations[0].getElementsByTagName('organization');
      if (organization.length > 0) {
        const titleElement = organization[0].getElementsByTagName('title');
        if (titleElement.length > 0 && titleElement[0].textContent) {
          this.courseTitle = titleElement[0].textContent.trim();
          return;
        }
      }
    }
    
    const titleElements = xmlDoc.getElementsByTagName('title');
    if (titleElements.length > 0) {
      for (let i = 0; i < titleElements.length; i++) {
        if (titleElements[i].textContent && titleElements[i].textContent.trim()) {
          this.courseTitle = titleElements[i].textContent.trim();
          return;
        }
      }
    }
    
    const metadata = xmlDoc.getElementsByTagName('metadata');
    if (metadata.length > 0) {
      const metadataScheme = metadata[0].getElementsByTagName('schema');
      if (metadataScheme.length > 0 && metadataScheme[0].textContent) {
        this.courseTitle = metadataScheme[0].textContent.trim();
        return;
      }
    }
    
    const items = xmlDoc.getElementsByTagName('item');
    if (items.length > 0) {
      const itemTitle = items[0].getElementsByTagName('title');
      if (itemTitle.length > 0 && itemTitle[0].textContent) {
        this.courseTitle = itemTitle[0].textContent.trim();
        return;
      }
    }
    
    if (!this.courseTitle) {
      this.courseTitle = 'SCORM Course';
    }
  }

  private async loadCourseTitleFromDatabase(tenantId: string): Promise<void> {
    try {
      const courseDocRef = doc(this.firestore, `schools/${tenantId}/courses/${this.courseId}`);
      const courseDocSnap = await getDoc(courseDocRef);
      
      if (courseDocSnap.exists()) {
        const courseData = courseDocSnap.data();
        if (courseData && courseData['title']) {
          this.courseTitle = courseData['title'];
          console.log('Found course title in database:', this.courseTitle);
        }
      }
    } catch (error) {
      console.error('Error loading course title from database:', error);
    }
  }

  // Launch course button handler
  launchScormContent() {
    console.log('==== LAUNCHING SCORM COURSE ====');
    
    if (!this.scormLaunchUrl) {
      this.scormError = 'Course launch URL is not available.';
      console.error('Cannot launch: scormLaunchUrl is not available');
      return;
    }
    
    // Clear any error message that might be displayed
    this.scormError = null;
    
    // Initialize the SCORM API on the window object
    this.scormApiService.setupScormAPI();
    
    // Get API calls for display
    this.apiCalls = this.scormApiService.getApiCalls();
    
    console.log('Creating safe URL from:', this.scormLaunchUrl);
    this.safeScormUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.scormLaunchUrl);
    this.scormLaunched = true;
    console.log('SCORM course launched, iframe URL set');
    
    // After the iframe is added to the DOM, inject the API into it
    setTimeout(() => {
      if (this.scormFrame && this.scormFrame.nativeElement) {
        const iframe = this.scormFrame.nativeElement;
        
        // Make sure the iframe can access the API
        iframe.setAttribute('allow', 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture');
        iframe.setAttribute('data-scorm-frame', 'true');
        
        // Inject the API into the iframe
        this.scormApiService.injectApiIntoIframe(iframe);
        
        // Set up periodic checks to ensure the API remains accessible
        this.apiCheckInterval = this.scormApiService.setupApiCheckInterval(iframe);
      }
    }, 1000);
    
    // Automatically trigger the SCORM API test after 30 seconds
    setTimeout(() => {
      console.log('Auto-triggering SCORM API test (30-second timer)');
      this.triggerScormApiTest();
    }, 30000);
    
    // 3-minute automatic completion removed – progress-based rule now handles imperfect packages
    
    console.log('==== SCORM COURSE LAUNCH COMPLETE ====');
  }

  /**
   * Toggles the browser full-screen mode for the SCORM iframe and notifies the shared service.
   * Can be called from template buttons or other components via the service.
   */
  toggleFullScreen(): void {
    const isCurrentlyFs = this.isFullScreen;

    if (!isCurrentlyFs) {
      this.enterFullScreen();
    } else {
      this.exitFullScreen();
    }
  }

  /** Helper to request browser full-screen */
  private enterFullScreen(): void {
    const iframeElNative = this.scormFrame?.nativeElement;
    if (!iframeElNative) {
      return;
    }

    const iframeEl: any = iframeElNative as any;
    const requestFn =
      iframeEl.requestFullscreen ||
      iframeEl.webkitRequestFullScreen ||
      iframeEl.webkitRequestFullscreen ||
      iframeEl.mozRequestFullScreen ||
      iframeEl.msRequestFullscreen;

    if (requestFn) {
      requestFn.call(iframeEl).catch((err: any) => console.error('Failed to enter full-screen', err));
    }
    this.isFullScreen = true;
    this.fullScreenToggle.setFullScreen(true);
  }

  /** Helper to exit browser full-screen */
  private exitFullScreen(): void {
    const doc: any = document as any;
    const exitFn =
      doc.exitFullscreen ||
      doc.webkitExitFullscreen ||
      doc.mozCancelFullScreen ||
      doc.msExitFullscreen;

    if (exitFn) {
      try {
        const result = exitFn.call(doc);
        if (result && typeof result.catch === 'function') {
          result.catch((err: any) => console.error('Failed to exit full-screen', err));
        }
      } catch (err) {
        console.error('Failed to exit full-screen', err);
      }
    }
    this.isFullScreen = false;
    this.fullScreenToggle.setFullScreen(false);
  }

  // Keep shared state updated when user exits via ESC or browser UI
  @HostListener('document:fullscreenchange')
  @HostListener('document:webkitfullscreenchange')
  @HostListener('document:mozfullscreenchange')
  @HostListener('document:MSFullscreenChange')
  onFullScreenChange(): void {
    const doc: any = document as any;
    const isFs = !!(
      doc.fullscreenElement ||
      doc.webkitFullscreenElement ||
      doc.mozFullScreenElement ||
      doc.msFullscreenElement
    );
    this.isFullScreen = isFs;
    this.fullScreenToggle.setFullScreen(isFs);
  }
} 